<script setup lang="ts">
import { ref, watch, type PropType, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation } from '@tanstack/vue-query';
import { type PatientFilter } from '@/api/patients';
import { type CaregiverFilter } from '@/api/caregivers';
import { shiftsApi } from '@/api/shifts';
import type { AxiosError } from 'axios';
import { useFilters } from '@/composables/useFilters';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const { hasPermission } = usePermissions();
const { trackShiftEvent } = useAnalytics();

const props = defineProps({
  patient: {
    type: Object as PropType<PatientFilter>,
    required: true,
  },
  caregiver: {
    type: Object as PropType<CaregiverFilter>,
    default: undefined,
  },
});

function futureDateValidator() {
  return yup.string().required(t('shiftForm.fieldRequired')).test(
    'future-date',
    t('shiftBatchDestroyForm.oldDateError'),
    (value) => {
      if (!value) {
        return true;
      }
      const selectedDate = new Date(value.replace(/-/g, '/'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return selectedDate >= today;
    },
  );
}

const validationSchema = yup.object({
  lastDate: props.caregiver?.id ?
    futureDateValidator() : yup.string().required(t('shiftForm.fieldRequired')),
});

const initialValues = {
  patientId: props.patient.id,
  lastDate: undefined,
  caregiverId: props.caregiver?.id,
};

const { handleSubmit, values, errors, defineField } = useForm({ validationSchema, initialValues });

const [lastDate, lastDateAttrs] = defineField('lastDate');

const queryParams = useFilters(values);

const { mutate, isError, error, isPending, isSuccess } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.destroy(undefined, queryParams.value),
    onSuccess: () => {
      const eventType = props.caregiver ?
        AnalyticsEvents.shifts.stoppedCaregiverMenu :
        AnalyticsEvents.shifts.stoppedCalendar;
      trackShiftEvent(eventType, { patientId: props.patient.id, caregiverId: props.caregiver?.id });
      emit('refetch');
      emit('success');
    },
  },
);

const submitButtonDisabled = ref(false);
const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  mutate();
});

const minDate = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (props.caregiver?.id) {
    return today.toISOString().split('T')[0];
  }

  return '';
});

watch(isSuccess, async () => {
  if (isSuccess.value === true) {
    const waitMs = 1500;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    emit('close');
  }
});
</script>
<template>
  <v-card
    v-if="hasPermission('shifts', 'destroy')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('shiftBatchDestroyForm.title') }}
      <template v-if="props.caregiver?.fullName">
        {{ t('shiftBatchDestroyForm.addTitle', { caregiver: props.caregiver.fullName }) }}
      </template>
    </v-card-title>
    <v-form
      v-if="!isPending && !isSuccess"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          <template v-if="props.caregiver?.fullName">
            {{ t('shiftBatchDestroyForm.explanationWithCaregiver', { caregiver: props.caregiver.fullName }) }}
          </template>
          <template v-else>
            {{ t('shiftBatchDestroyForm.explanation') }}
          </template>
        </v-card-text>
        <div class="d-flex justify-start">
          <p class="mx-4 mb-4 text-caption">
            <v-icon
              icon="mdi-information"
              class="mb-1"
            />
            ¿Qué significa que el turno se puede borrar?
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              <p>El turno no tiene:</p>
              <p v-if="!hasPermission('marked_shifts', 'destroy')">
                Su estado actualizado
              </p>
              <p>Registros escritos</p>
              <p>Registros visuales</p>
              <p>Registros de signos vitales</p>
            </v-tooltip>
          </p>
        </div>
        <v-text-field
          v-model="lastDate"
          type="date"
          v-bind="lastDateAttrs"
          variant="outlined"
          :label="t('shiftBatchDestroyForm.lastDate')"
          :min="minDate"
          :error-messages="errors.lastDate"
        />
        <v-card-text v-if="!errors.lastDate && lastDate">
          <template v-if="props.caregiver?.fullName">
            {{ t('shiftBatchDestroyForm.confirmationWithCaregiver', { caregiver: props.caregiver.fullName }) }}
          </template>
          <template v-else>
            {{ t('shiftBatchDestroyForm.confirmation') }}
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text="Cerrar"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            v-if="!errors.lastDate && lastDate"
            color="red"
            type="submit"
            :disabled="submitButtonDisabled"
          >
            {{ t('shiftBatchDestroyForm.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-col
      v-if="isPending"
      class="pb-20"
    >
      <v-row class="justify-center d-flex">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="primary"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Estamos eliminando los turnos, esto puede tomar unos segundos...</p>
      </v-row>
    </v-col>
    <v-col v-if="isSuccess">
      <v-row class="justify-center d-flex">
        <v-icon
          icon="mdi-check-circle"
          color="primary"
          size="50"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Turnos eliminados exitosamente!</p>
      </v-row>
    </v-col>
    <v-alert
      v-if="isError && error"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{
        error?.response?.status === 404
          ? t('shiftBatchDestroyForm.notFoundError')
          : t('userSession.defaultError')
      }}
    </v-alert>
  </v-card>
</template>
